/* eslint-disable max-lines */
import { CalendarOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons'
import { dateUtil } from '@salescore/buff-common'
import type { PropertyTypeEnum, ViewFieldsFragment } from '@salescore/client-api'
import { HUB_PROPERTY_TYPE_ICONS, useMe } from '@salescore/client-common'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  CORE_CONSTANT,
  createDimensionFieldName,
  fieldTypeSchema,
  generateLabelSqlFieldName,
  timeframeSchema,
  type ViewConfigDimension,
  type ViewConfigKpiParameter,
  type ViewKpiAppearance,
  type ViewQueryField,
  type ViewQueryRecordNode,
  type ViewQueryResultUiPivotColumn,
  type ViewUiKpi,
  type ViewUiKpiPivot,
  type ViewUiKpiPivotColumn,
} from '@salescore/core'
import type { OrganizationSetting } from '@salescore/features'
import { Avatar } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { Array, pipe, Record, Result } from 'effect'
import { t } from 'i18next'
import type { ReactElement } from 'react'
import { useRecoilValue } from 'recoil'

import type { PivotColumn, PivotRowColumn, PivotSystemColumn } from '../domain/column'
import { findPivotColumnKpiParameter } from '../domain/findPivotColumnKpiParameter'
import { findPivotRowColumnNode, findPivotValueColumnNode, isNextColumnInSameBlock } from '../domain/pivotColumnLayout'
import { kpiParameterAtom, kpiUserAppearanceAtom } from '../recoil/navigation/atoms'
import { additionalConfigAtom } from '../recoil/records/atoms'
import { configAtom } from '../recoil/viewConfig'
import { RSheetsStyle } from '../rsheet/util/RSheetsStyle'
import { flatNodes } from '../state/nodeUtility'
import { useViewQueryResult } from './useViewQueryResult'

const COLUMN_WIDTH = 100
const MAX_COLUMN_KEYS = 100 // 列の仮想化ができておらず、多すぎるとブラウザが落ちるため制限する

export const usePivotColumns = (
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  view: ViewFieldsFragment,
): {
  columnKeys: ColumnKey[][]
  systemColumns: Array<PivotSystemColumn<ViewQueryRecordNode>>
  rowColumns: Array<PivotRowColumn<ViewQueryRecordNode>>
  pivotColumns: Array<PivotColumn<ViewQueryRecordNode>>
} => {
  const empty = {
    columnKeys: [],
    systemColumns: [],
    rowColumns: [],
    pivotColumns: [],
    columns: [],
  }
  const config = useRecoilValue(configAtom)
  const additionalConfig = useRecoilValue(additionalConfigAtom)
  const me = useMe()
  const viewQueryResult = useViewQueryResult(view)
  const kpiParameter = useRecoilValue(kpiParameterAtom)
  const { unlimitedColumnsEnabled } = useRecoilValue(kpiUserAppearanceAtom)
  const [component] = compiledConfig.ui
  if (component.type !== 'KpiPivotSheet' && component.type !== 'KpiSheet') {
    return empty
  }
  const query = viewQueryResult?.viewQuery
  if (viewQueryResult?.ui?.pivotColumns === undefined || query?.type !== 'multiTablePivot') {
    // ありえないはず
    return empty
  }

  // ない方が理想的な気がするが、cursor側のロジックで行番号があることを前提にしてしまっているため、一旦追加しておく
  const systemColumns: Array<PivotSystemColumn<ViewQueryRecordNode>> = [
    {
      columnType: 'pivot_system',
      index: 0,
      configIndex: undefined,
      isSystem: true,
      node: query.tree,
      key: `rowIndex`,
      width: 28,
      type: `rowIndex`,
      value: (): void => {
        // empty
      },
    },
  ]

  const nodes = flatNodes(query.tree)

  const rowColumns = Array.filterMap(
    component.pivot.rows,
    (row, index): Result.Result<PivotRowColumn<ViewQueryRecordNode>, void> =>
      Result.map(findPivotRowColumnNode(nodes, index), (node): PivotRowColumn<ViewQueryRecordNode> => {
        const fieldName = createDimensionFieldName(index + 1)
        const field: ViewQueryField = {
          name: fieldName,
          nodePath: node.path,
          read: {
            sql: ``,
          },
          meta: {
            label: ``,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion,sonarjs/todo-tag
            fieldType: row.fieldType as 'string', // TODO
            dependedPropertyNamesWithStreamName: [],
          },
        }
        const ui = component.appearance?.rows?.find((x) => x.key === row.key)
        const dimensionFilter = (additionalConfig.kpiParameter?.dimensionFilterLeafs ?? []).find(
          (x) => x.type === 'dimension' && x.dimension.key === row.key,
        )
        const configIndex = kpiParameter.pivot.rows.findIndex((x) => x.key === row.key)

        return {
          columnType: 'pivot_row',
          index: index + systemColumns.length,
          configIndex: configIndex === -1 ? undefined : configIndex, // additionalConfig.kpiParameter.pivot.rows におけるindex
          type: row.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key ? `rowKpi` : row.fieldType,
          field,
          metaType: row.fieldMetaType,
          selectOptions: row.selectOptions,
          node,
          key: `field-${fieldName}`,
          title: row.label,
          // smallTitle: `(行)`,
          // nodeBlock: {
          //   nodePath: [`row`],
          //   labels: [''],
          //   colors: [`blue`],
          // },
          isFiltered: dimensionFilter !== undefined,
          width: ui?.width ?? COLUMN_WIDTH,
          readonly: true,
          icon: getRowIcon(row),
          value: (): void => {
            // 使ってないので消したい
          },
          label: (recordNode: ViewQueryRecordNode): unknown =>
            recordNode.attributes[generateLabelSqlFieldName(fieldName)],
        }
      }),
  )

  const isKpiColumn =
    kpiParameter.pivot.columns.length === 1 &&
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    kpiParameter.pivot.columns[0]!.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key
  const kpis = component.type === 'KpiPivotSheet' ? component.kpis : []
  const grouped = pipe(
    kpis,
    Array.groupBy((x) => x.groupName ?? CORE_CONSTANT.KPI_GROUP_DEFAULT_NAME),
    Record.map((vs) =>
      vs.map((x) => ({
        value: x.id,
        label: x.label,
      })),
    ),
  )
  const groups = Array.dedupe(kpis.map((x) => x.groupName ?? CORE_CONSTANT.KPI_GROUP_DEFAULT_NAME)) // KPIの順番をグループの順番とする
  const kpiColumnIndex = component.pivot.columns.findIndex((x) => x.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key)
  const kpiGroupColumnIndex = component.pivot.columns.findIndex(
    (x) => x.key === CORE_CONSTANT.KPI_PIVOT_KPI_GROUP_DIMENSION().key,
  )
  const isKpiWithGroup = kpiColumnIndex === kpiGroupColumnIndex + 1 && kpiGroupColumnIndex !== -1
  // NOTE: cartesianProductは、空配列の場合は空配列を返すので、columnがない場合も1つはcolumnColumnsが作成される
  // NOTE: 2022/12 columnKeysは基本的にサーバー側の実行結果(viewQueryResult)より計算するが、KPIの並び替えをUIから行ったときに都度通信が走ると重く、
  //               また不具合により何故かrefetchが走らないこともあり、KPI列単体の表示の時は、component.kpisの情報を使ってcolumnKeysを求めてしまい、表示を高速化する
  const columnKeys = isKpiColumn
    ? kpis.map((kpi) => [{ value: kpi.id, label: kpi.label }])
    : cartesianProductForKpi(
        viewQueryResult.ui.pivotColumns,
        // eslint-disable-next-line sonarjs/no-nested-conditional
        isKpiWithGroup ? kpiColumnIndex : -1,
        // eslint-disable-next-line sonarjs/no-nested-conditional
        isKpiWithGroup ? kpiGroupColumnIndex : -1,
        groups,
        grouped,
        me.organization.setting,
        kpiParameter.pivot.columns,
      )
  const pivotColumns = Array.filterMap(
    unlimitedColumnsEnabled === true ? columnKeys : columnKeys.slice(0, MAX_COLUMN_KEYS),

    (valuesWithLabelChunk, index): Result.Result<PivotColumn<ViewQueryRecordNode>, void> =>
      Result.map(findPivotValueColumnNode(nodes), (node): PivotColumn<ViewQueryRecordNode> => {
        // eslint-disable-next-line sonarjs/todo-tag
        // const columnLabels = columnValues.map((x) => forceString(x)) // TODO: referenceの対応
        const columnLabels = valuesWithLabelChunk.map((x) => x.label ?? x.value)
        const columnValues = valuesWithLabelChunk.map((x) => x.value)
        const fieldName = JSON.stringify([...columnValues, 'actual'])
        const field: ViewQueryField = {
          name: fieldName,
          nodePath: node.path,
          read: {
            sql: ``,
          },
          meta: {
            label: ``,
            fieldType: `string`,
            dependedPropertyNamesWithStreamName: [],
          },
        }
        const kpiView = component.type === 'KpiSheet' ? view : undefined

        const sorter = kpiParameter.sorter?.columnKeys?.find((x) => x.key === field.name)
        const lastColumn = component.pivot.columns.at(-1)
        const lastColumnValue = columnValues.at(-1)
        const ui = component.appearance?.columns
          ?.find((x) => x.key === lastColumn?.key)
          ?.keys.find((x) => x.value === lastColumnValue)
        const isTotalColumn = columnValues.includes(CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING)

        const isNextSameBlock = isNextColumnInSameBlock(valuesWithLabelChunk, columnKeys[index + 1])

        // view.config.type === 'kpi' の時 configIndex=undefined のため、 KPI ピボットでは集計列の移動はできなくなっている
        const configIndex =
          lastColumnValue !== undefined && config.type === 'kpiPivot'
            ? config.kpis.findIndex((x) => x.viewId === lastColumnValue)
            : undefined

        return {
          columnType: 'pivot',
          index: index + systemColumns.length + rowColumns.length,
          configIndex: configIndex === -1 ? undefined : configIndex, // config.kpis におけるindex
          type: 'kpi',
          field,
          node,
          key: fieldName,
          title: columnLabels.at(-1) ?? kpiView?.name ?? '値', // labelsがないとき=列を指定していない時。KPIビューの時はKPI名(=ビュー名)を表示する。
          smallTitle: component.pivot.columns.at(-1)?.label,
          nodeBlock: {
            nodePath: columnValues.slice(0, -1),
            labels: columnLabels.slice(0, -1),
            colors: columnLabels.map(() => `gray` as const).slice(0, -1),
            smallLabels: component.pivot.columns.map((x) => x.label),
            helpTexts: columnValues
              .map((_value, index) => getColumnHelpText(columnValues, index, component, kpiParameter))
              .slice(0, -1),
          },
          icon: getColumnIcon(columnValues, columnValues.length - 1, component, kpiParameter),
          helpText: getColumnHelpText(columnValues, columnValues.length - 1, component, kpiParameter),
          width: ui?.width ?? COLUMN_WIDTH,
          readonly: true,
          sortState: sorter?.order,
          cellBackgroundColor: isTotalColumn ? RSheetsStyle.color.kpiTotalCell : undefined,
          kpiBorderRight: !isNextSameBlock,
          value(): void {
            // eslint-disable-next-line sonarjs/todo-tag
            // TODO: 消し去りたい
          },
          label(recordNode: ViewQueryRecordNode): unknown {
            return recordNode.attributes[generateLabelSqlFieldName(fieldName)]
          },
        }
      }),
  )

  return {
    columnKeys,
    systemColumns,
    rowColumns,
    pivotColumns,
  }
}

const getColumnIcon = (
  columnValues: string[],
  columnIndex: number,
  component: ViewUiKpi | ViewUiKpiPivot,
  kpiParameter: ViewConfigKpiParameter,
  // eslint-disable-next-line sonarjs/function-return-type
): string | ReactElement | undefined => {
  const column = kpiParameter.pivot.columns[columnIndex]
  // const column = component.pivot.columns[columnIndex] // こっちのfieldTypeはkpiなので注意
  if (column === undefined) {
    return
  }
  if (column.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key && component.type === 'KpiPivotSheet') {
    const columnValue = columnValues[columnIndex]
    if (columnValue === undefined) {
      return
    }
    if (kpiParameter.goalConfig?.commitmentKpiLabel?.viewId === columnValue) {
      return <Avatar size="large" src="/images/logo-icon.png" />
    }

    return component.kpis.find((x) => x.id === columnValue)?.icon
  }
  // kpiじゃない場合は、型情報に合わせてiconを設定
  return HUB_PROPERTY_TYPE_ICONS[column.fieldType]
}

const KPI_ACHIEVEMENT_STATUS_TARGET_VALUE_ENUM = {
  averageProgress: t(`目標の標準進捗`),
  goal: t(`目標`),
  zero: t(`ゼロ`),
}

const getColumnHelpText = (
  columnValues: string[],
  columnIndex: number,
  component: ViewUiKpi | ViewUiKpiPivot,
  kpiParameter: ViewConfigKpiParameter,
): string | undefined => {
  const column = kpiParameter.pivot.columns[columnIndex]
  if (column === undefined) {
    return
  }

  // KPIのとき
  if (!(column.key === CORE_CONSTANT.KPI_PIVOT_KPI_DIMENSION.key && component.type === 'KpiPivotSheet')) {
    return undefined
  }

  const columnValue = columnValues[columnIndex]
  if (columnValue === undefined) {
    return
  }

  const kpi = component.kpis.find((x) => x.id === columnValue)
  if (kpi === undefined) {
    return undefined
  }

  return getKpiHelpText(kpi)
}

export const getKpiHelpText = (kpi: ViewKpiAppearance): string | undefined => {
  if (kpi.description !== undefined) {
    return kpi.description
  }

  const lessthan = kpi.achievementStatusCalculationType === `greaterThanOrEqual` ? t(`以上`) : t(`以下`)
  const valueType = KPI_ACHIEVEMENT_STATUS_TARGET_VALUE_ENUM[kpi.achievementStatusTargetValueType ?? 'averageProgress']

  return t(`実績が{{valueType}}{{lessthan}}のときに緑色になります。`, { valueType, lessthan })
}

const getRowIcon = (row: ViewUiKpiPivotColumn): string | ReactElement | undefined => {
  switch (row.fieldType) {
    case 'user': {
      return <UserOutlined />
    }
    case 'date': {
      return <CalendarOutlined />
    }
    default: {
      break
    }
  }
  if (row.key.startsWith(`p_salescore_user_groups_d`)) {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO
    return <TeamOutlined />
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return HUB_PROPERTY_TYPE_ICONS[row.fieldType as PropertyTypeEnum]
}

export interface ColumnKey {
  value: string
  label?: string
}

type ColumnKeyWithFieldName = ColumnKey & { kpiParameter: ViewConfigDimension | undefined }

const cartesianProductForKpi = (
  array: ViewQueryResultUiPivotColumn[],
  kpiIndex: number,
  kpiGroupIndex: number,
  kpiGroups: string[],
  kpiGroupToKpis: Record<string, ColumnKey[]>,
  settings: OrganizationSetting,
  kpiParameterColumns: ViewConfigDimension[],
  // eslint-disable-next-line @typescript-eslint/max-params
): ColumnKey[][] =>
  array
    .map((column) => {
      // kpiParameterがないケースがあるかどうか網羅的に考えられていない
      const kpiParameter = findPivotColumnKpiParameter(kpiParameterColumns, column.columnFieldName)
      return { ...column, kpiParameter }
    })
    .reduce<ColumnKeyWithFieldName[][]>(
      (a, b, index) =>
        a.flatMap((x) => {
          if (index === kpiGroupIndex) {
            return kpiGroups.map((kpiGroup) => x.concat({ value: kpiGroup, kpiParameter: b.kpiParameter }))
          }
          if (index === kpiIndex) {
            const kpiGroup = x.at(-1)?.value ?? ''
            const kpis = kpiGroupToKpis[kpiGroup] ?? []
            return kpis.map((y) => x.concat({ ...y, kpiParameter: b.kpiParameter }))
          }
          return b.valuesWithLabel
            .filter((y) => {
              // 日付でない場合、無条件に表示する
              if (b.kpiParameter?.fieldType !== fieldTypeSchema.enum.date) {
                return true
              }
              // 日付の値が空の場合、表示しない
              if (y.value === '') {
                return false
              }
              // 日付の場合、親列の日付範囲に含まれるかどうかを判定する
              return isDisplayColumn({
                // eslint-disable-next-line max-nested-callbacks
                parentDateColumns: x.filter((column) => column.kpiParameter?.fieldType === fieldTypeSchema.enum.date),
                childColumn: { ...y, kpiParameter: b.kpiParameter },
                settings,
              })
            })
            .map((y) => x.concat({ ...y, kpiParameter: b.kpiParameter }))
        }),
      [[]],
    )
    .map((x) => x.map((y) => ({ value: y.value, label: y.label })))

const isDisplayColumn = ({
  childColumn,
  parentDateColumns,
  settings,
}: {
  childColumn: ColumnKeyWithFieldName
  parentDateColumns: ColumnKeyWithFieldName[]
  settings: OrganizationSetting
}): boolean =>
  parentDateColumns
    .filter((x) => isRequireDateRangeCheck({ parentColumn: x, childColumn }))
    .every((x) => checkDateRange({ parentColumn: x, childColumn, settings }))

const isRequireDateRangeCheck = ({
  parentColumn,
  childColumn,
}: {
  parentColumn: ColumnKeyWithFieldName
  childColumn: ColumnKeyWithFieldName
}): boolean => {
  const { weekday, hour } = timeframeSchema.enum
  const parentTimeframe = parentColumn.kpiParameter?.dateSpan
  const childTimeframe = childColumn.kpiParameter?.dateSpan
  // ありえないはずだが、一応
  if (parentTimeframe === undefined || childTimeframe === undefined) {
    return false
  }
  // 子列が小計列の場合は、全ての場合で表示する
  if (childColumn.value === CORE_CONSTANT.KPI_PIVOT_TOTAL_STRING) {
    return false
  }
  // 子列が曜日列・時間列の場合は、全ての時間範囲に含まれるので、必ず表示する
  if (childTimeframe === weekday || childTimeframe === hour) {
    return false
  }
  // それ以外は、親列の時間範囲に含まれるかどうかを判定する
  return true
}

const checkDateRange = ({
  parentColumn,
  childColumn,
  settings,
}: {
  parentColumn: ColumnKeyWithFieldName
  childColumn: ColumnKeyWithFieldName
  settings: OrganizationSetting
}): boolean => {
  // eslint-disable-next-line @typescript-eslint/naming-convention,sonarjs/no-dead-store,sonarjs/no-unused-vars
  const { year, fiscal_year, fiscal_half, fiscal_quarter, month, week, weekday, day } = timeframeSchema.enum
  const parentTimeframe = parentColumn.kpiParameter?.dateSpan
  const childTimeframe = childColumn.kpiParameter?.dateSpan
  if (parentTimeframe === undefined || childTimeframe === undefined) {
    return false
  }

  // 親列が曜日かつ子列が日の場合のみ、曜日の比較を行う
  if (parentTimeframe === weekday && childTimeframe === day) {
    return compareWeekday({ parentColumn, childColumn })
  }

  const { accountClosingMonth } = settings
  const parentDateRange = getDateRange({ column: parentColumn, accountClosingMonth })
  const childDateRange = getDateRange({ column: childColumn, accountClosingMonth })
  if (parentDateRange === undefined || childDateRange === undefined) {
    return false
  }

  // その他の場合、期間が重なっているかどうかを判定する
  return isDateRangeOverlapped(parentDateRange, childDateRange)
}

const getDateRange = ({
  column,
  accountClosingMonth,
}: {
  column: ColumnKeyWithFieldName
  accountClosingMonth: number
}): { startAt: dayjs.Dayjs; endAt: dayjs.Dayjs } | undefined => {
  const { kpiParameter, value } = column
  const timeframe = kpiParameter?.dateSpan

  switch (timeframe) {
    case timeframeSchema.enum.year:
    case timeframeSchema.enum.month:
    case timeframeSchema.enum.week:
    case timeframeSchema.enum.day: {
      return dateUtil.parse(value)
    }
    case timeframeSchema.enum.fiscal_quarter:
    case timeframeSchema.enum.fiscal_half: {
      return dateUtil.parse(value, { accountClosingMonth })
    }
    case timeframeSchema.enum.fiscal_year: {
      return dateUtil.parse(value, { accountClosingMonth, timeframe: 'fiscal_year' })
    }
    default: {
      break
    }
  }
}

const isDateRangeOverlapped = (
  parentDateRange: { startAt: Dayjs; endAt: Dayjs },
  childDateRange: { startAt: Dayjs; endAt: Dayjs },
): boolean =>
  dateUtil.compare(parentDateRange.startAt, childDateRange.endAt, '<=') &&
  dateUtil.compare(parentDateRange.endAt, childDateRange.startAt, '>=')

const compareWeekday = ({
  parentColumn,
  childColumn,
}: {
  parentColumn: ColumnKeyWithFieldName
  childColumn: ColumnKeyWithFieldName
}): boolean => {
  const parentWeekday = parentColumn.value.split('_')[0] // d_dddの形を期待 (ex: 2_Mon)
  const childWeekday = dayjs(childColumn.value).day()
  // parentWeekdayはPostgresのTO_CHAR(x, 'D')の結果と同じなので、1~7の範囲になる
  // dayjsのday()は0~6の範囲なので、+1して比較する
  return parentWeekday === `${childWeekday + 1}`
}
